import React, { useContext, useEffect, useState } from "react";

import { useAnalyticsTracker } from "components/instrumentation/source/analytics";
import { useBootstrap } from "components/store/source";

import { usePaymentGatewaysPublishableKeys } from "hooks/payments/usePaymentGatewaysPublishableKeys";
import { getStripeInstance } from "services/stripe/getStripeInstance";
import { PaymentGateway } from "types/graphql";

export const ChargebudContext = React.createContext(undefined);
export const ChargebudProvider = ChargebudContext.Provider;

interface Props {
  paymentInfo: any;
  children: React.ReactNode;
}

function Chargebud({ paymentInfo, children }: Props) {
  const [state, setState] = useState({
    stripePublishKey: null,
    stripePromise: null,
    adyenClientKey: null,
  });

  const {
    featureFlags: { bnplListingUpsellEnabled },
  } = useBootstrap();

  const { stripePublishKey, adyenClientKey } =
    usePaymentGatewaysPublishableKeys();

  const { analytics } = useAnalyticsTracker();

  const stripePaymentElementCreationDetail =
    paymentInfo?.paymentCreationDetails?.find(
      paymentCreationDetail =>
        paymentCreationDetail.gatewayName == PaymentGateway.Stripe,
    );
  const stripePaymentElementEnabled = !!stripePaymentElementCreationDetail;

  const paypalCreationDetail = paymentInfo?.paymentCreationDetails?.find(
    paymentCreationDetail =>
      paymentCreationDetail.gatewayName == PaymentGateway.AdyenPaypal,
  );

  const paypalEnabled = !!paypalCreationDetail;

  useEffect(() => {
    let mounted = true;

    const initChargebud = async () => {
      analytics.track("Chargebud initialization", {
        status: "started",
      });

      if (stripePublishKey && adyenClientKey && mounted) {
        setState({
          adyenClientKey: adyenClientKey,
          stripePublishKey: stripePublishKey,
          stripePromise: getStripeInstance(stripePublishKey),
        });
        analytics.track("Chargebud initialization", {
          status: "finished",
        });
      }
    };

    if (bnplListingUpsellEnabled) {
      initChargebud();
    }

    return () => {
      mounted = false;
    };
    // We can't add analytics to the dependency array because it changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ChargebudProvider //TODO: maybe refactor and export config { stripePaymentElementEnabled, paypalEnabled, }
      value={{ ...state, stripePaymentElementEnabled, paypalEnabled }}
    >
      {children}
    </ChargebudProvider>
  );
}

export default Chargebud;

export const useChargebud = () => {
  const context = useContext(ChargebudContext);

  if (context === undefined) {
    throw new Error("useChargebud must be used within a ChargebudProvider");
  }

  return context;
};

// eslint-disable-next-line react/display-name
export const withChargebudContext = WrappedComponent => props => {
  return (
    <Chargebud {...props}>
      <WrappedComponent {...props} />
    </Chargebud>
  );
};
